import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Snackbar,
  Alert,
  Typography,
  styled,
  alpha
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Share,
  CalendarToday,
  FolderOutlined,
  TagOutlined,
  BookmarkBorder
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

// Styled Glassmorphic Paper Container
const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(16px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.5)',
  color: '#f8fafc',
}));

// Styled Modern Blog Card
const BlogCard = styled(Card)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '18px',
  color: '#f8fafc',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-6px)',
    border: '1px solid rgba(59, 130, 246, 0.4)',
    boxShadow: '0 12px 30px -10px rgba(59, 130, 246, 0.25)',
    background: 'rgba(15, 23, 42, 0.85)',
    '& .card-media': {
      transform: 'scale(1.05)',
    },
    '& .blog-title': {
      color: '#3b82f6',
    },
  },
}));

// Styled HTML Content Container for Rich Article Rendering
const ArticleBody = styled(Box)(({ theme }) => ({
  color: '#cbd5e1',
  fontSize: '1.05rem',
  lineHeight: 1.8,
  '& p': {
    marginBottom: '1.5rem',
  },
  '& h1, & h2, & h3, & h4': {
    color: '#f8fafc',
    fontWeight: 800,
    marginTop: '2rem',
    marginBottom: '1rem',
    lineHeight: 1.3,
  },
  '& h2': { fontSize: '1.75rem', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.5rem' },
  '& h3': { fontSize: '1.35rem' },
  '& a': {
    color: '#3b82f6',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'all 0.2s',
    '&:hover': {
      borderBottomColor: '#3b82f6',
    },
  },
  '& code': {
    background: 'rgba(59, 130, 246, 0.12)',
    color: '#60a5fa',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '0.9em',
    fontFamily: 'monospace',
  },
  '& pre': {
    background: '#030712',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '16px',
    overflowX: 'auto',
    mb: '1.5rem',
    '& code': {
      background: 'transparent',
      padding: 0,
      color: '#e2e8f0',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '16px',
    margin: '1.5rem 0',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  '& blockquote': {
    borderLeft: '4px solid #3b82f6',
    margin: '1.5rem 0',
    paddingLeft: '1.25rem',
    fontStyle: 'italic',
    color: '#94a3b8',
    background: 'rgba(59, 130, 246, 0.04)',
    paddingTop: '8px',
    paddingBottom: '8px',
    borderRadius: '0 12px 12px 0',
  },
  '& ul, & ol': {
    paddingLeft: '1.5rem',
    marginBottom: '1.5rem',
    '& li': {
      marginBottom: '0.5rem',
    },
  },
}));

const BlogReader = () => {
  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const baseUrl = 'https://vikashtiwaridev.web.app';

  useEffect(() => {
    const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPosts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // Sync routeId with selected post
  useEffect(() => {
    if (!posts.length) return;
    if (routeId) {
      const found = posts.find((p) => p.id === routeId);
      if (found) {
        setSelected(found);
      } else {
        setSelected(null);
      }
    } else {
      setSelected(null);
    }
  }, [routeId, posts]);

  const handleSelectPost = (post) => {
    setSelected(post);
    if (post) {
      navigate(`/blog/${post.id}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/blog');
    }
  };

  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const selectedCat = (category || 'All').toString().trim().toLowerCase();

    return posts.filter((p) => {
      const pCat = (p.category || 'General').toString().trim().toLowerCase();
      if (selectedCat !== 'all' && pCat !== selectedCat) return false;

      if (!q) return true;
      const hay = [p.title, p.excerpt, (p.seoKeywords || []).join(' '), p.content]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [posts, searchQuery, category]);

  const categories = useMemo(() => {
    const set = new Set(['All', 'General', 'Technology', 'Design', 'Lifestyle', 'Business', 'Education']);
    posts.forEach((p) => set.add(p.category || 'General'));
    return Array.from(set);
  }, [posts]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    let date = null;
    if (typeof timestamp.toDate === 'function') date = timestamp.toDate();
    else if (timestamp && timestamp.seconds) date = new Date(timestamp.seconds * 1000);
    else date = new Date(timestamp);

    try {
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
    } catch (e) {
      return String(date);
    }
  };

  const handleShare = async (post) => {
    if (!post) return;
    const shareUrl = `${baseUrl}/blog/${post.id}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt || '', url: shareUrl });
        setSnack({ open: true, message: 'Shared successfully', severity: 'success' });
        return;
      } catch (e) {
        // Fallback to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnack({ open: true, message: 'Link copied to clipboard', severity: 'success' });
    } catch (e) {
      window.prompt('Copy this link', shareUrl);
      setSnack({ open: true, message: 'Unable to copy automatically — prompt shown', severity: 'warning' });
    }
  };

  const handleCloseSnack = () => setSnack((s) => ({ ...s, open: false }));

  // SEO Data Generation
  const currentCanonicalUrl = selected ? `${baseUrl}/blog/${selected.id}` : `${baseUrl}/blog`;
  const pageTitle = selected
    ? `${selected.title} | Vikash Tiwari Tech Insights`
    : 'Technical Articles & Case Studies | Vikash Tiwari - Software Engineer';
  const pageDescription = selected
    ? (selected.excerpt || (selected.content || '').replace(/<[^>]*>/g, '').slice(0, 160))
    : 'In-depth tutorials, software architecture breakdowns, Android Kotlin, Flutter, and Next.js engineering insights by Vikash Tiwari.';
  const pageImage = selected?.image || `${baseUrl}/VikashTiwariIcon.png`;

  // Schema Markup
  const structuredData = selected
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        'headline': selected.title,
        'description': pageDescription,
        'image': selected.image ? [selected.image] : [],
        'datePublished': selected.createdAt?.seconds
          ? new Date(selected.createdAt.seconds * 1000).toISOString()
          : new Date().toISOString(),
        'author': {
          '@type': 'Person',
          'name': 'Vikash Tiwari',
          'url': baseUrl,
        },
        'publisher': {
          '@type': 'Person',
          'name': 'Vikash Tiwari',
        },
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': currentCanonicalUrl,
        },
        'keywords': (selected.seoKeywords || []).join(', '),
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        'name': 'Vikash Tiwari Tech Blog',
        'description': pageDescription,
        'url': currentCanonicalUrl,
        'author': {
          '@type': 'Person',
          'name': 'Vikash Tiwari',
        },
      };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={currentCanonicalUrl} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        {selected?.seoKeywords?.length > 0 && (
          <meta name="keywords" content={selected.seoKeywords.join(', ')} />
        )}

        {/* Open Graph */}
        <meta property="og:type" content={selected ? 'article' : 'website'} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={currentCanonicalUrl} />
        <meta property="og:image" content={pageImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        {/* Structured JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', background: '#030712', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
          {/* Header Banner */}
          <Box sx={{ mb: 6, textAlign: { xs: 'left', md: 'center' }, maxWidth: '800px', mx: 'auto' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#3b82f6',
                fontWeight: 900,
                letterSpacing: 3,
                fontSize: '0.8rem',
                display: 'block',
                mb: 1,
              }}
            >
              KNOWLEDGE HUB & ARTICLES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                color: '#f8fafc',
                fontSize: { xs: '2.2rem', md: '3.2rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
              }}
            >
              Engineering <span style={{ color: '#3b82f6' }}>Perspectives.</span>
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#94a3b8', mt: 2, fontSize: '1.05rem', lineHeight: 1.6 }}
            >
              Architectural deep-dives, mobile hardware integrations, software tutorials, and technological insights.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Sidebar Controls & Mini List */}
            <Grid item xs={12} md={4} lg={3.5}>
              <GlassPaper sx={{ p: 3, position: { md: 'sticky' }, top: { md: 90 } }}>
                {/* Search Field */}
                <TextField
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: '#f8fafc',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(59, 130, 246, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                    },
                  }}
                />

                {/* Category Pills Header */}
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#94a3b8', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}
                >
                  Categories
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 3 }}>
                  {categories.map((c) => {
                    const isSelected = c === category;
                    return (
                      <Chip
                        key={c}
                        label={c}
                        clickable
                        onClick={() => setCategory(c)}
                        sx={{
                          bgcolor: isSelected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.04)',
                          color: isSelected ? '#60a5fa' : '#cbd5e1',
                          border: isSelected ? '1px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.08)',
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: '0.8rem',
                          borderRadius: '8px',
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.15)',
                          },
                        }}
                      />
                    );
                  })}
                </Stack>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 2 }} />

                {/* Compact Article List */}
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#94a3b8', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}
                >
                  Matching Articles ({filtered.length})
                </Typography>

                <Stack spacing={2} sx={{ maxHeight: { md: '450px' }, overflowY: 'auto', pr: 0.5 }}>
                  {filtered.length === 0 ? (
                    <Typography color="#64748b" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                      No matching articles found.
                    </Typography>
                  ) : (
                    filtered.map((post) => {
                      const isCurrent = selected?.id === post.id;
                      return (
                        <Card
                          key={post.id}
                          elevation={0}
                          onClick={() => handleSelectPost(post)}
                          sx={{
                            display: 'flex',
                            bgcolor: isCurrent ? 'rgba(59, 130, 246, 0.12)' : 'rgba(255, 255, 255, 0.02)',
                            border: isCurrent ? '1px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.05)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            p: 1.25,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          {post.image ? (
                            <CardMedia
                              component="img"
                              image={post.image}
                              alt={post.title}
                              sx={{ width: 68, height: 68, borderRadius: '8px', objectFit: 'cover', mr: 1.5 }}
                            />
                          ) : null}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: isCurrent ? '#60a5fa' : '#f8fafc',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {post.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.3,
                                my: 0.5,
                                fontSize: '0.75rem',
                              }}
                            >
                              {post.excerpt || (post.content || '').replace(/<[^>]*>/g, '')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarToday sx={{ fontSize: '0.7rem' }} /> {formatDate(post.createdAt)}
                            </Typography>
                          </Box>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              </GlassPaper>
            </Grid>

            {/* Main Content Area */}
            <Grid item xs={12} md={8} lg={8.5}>
              <GlassPaper sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
                {!selected ? (
                  /* Grid List View */
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                        {category === 'All' ? 'Latest Posts' : `${category} Posts`}
                      </Typography>
                      <Chip
                        label={`${filtered.length} Items`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#94a3b8', fontWeight: 600 }}
                      />
                    </Stack>

                    <Grid container spacing={3}>
                      {filtered.map((post) => (
                        <Grid item xs={12} sm={6} key={post.id}>
                          <BlogCard onClick={() => handleSelectPost(post)}>
                            {post.image ? (
                              <Box sx={{ overflow: 'hidden', height: 180, position: 'relative' }}>
                                <CardMedia
                                  component="img"
                                  height="180"
                                  image={post.image}
                                  alt={post.title}
                                  className="card-media"
                                  sx={{ transition: 'transform 0.5s ease', objectFit: 'cover' }}
                                />
                                <Chip
                                  label={post.category || 'General'}
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    left: 12,
                                    bgcolor: 'rgba(3, 7, 18, 0.75)',
                                    color: '#3b82f6',
                                    backdropFilter: 'blur(8px)',
                                    fontWeight: 700,
                                    fontSize: '0.725rem',
                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                  }}
                                />
                              </Box>
                            ) : null}
                            <CardContent sx={{ p: 2.5 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#64748b', fontSize: '0.775rem', mb: 1 }}>
                                <CalendarToday sx={{ fontSize: '0.85rem' }} />
                                <span>{formatDate(post.createdAt)}</span>
                              </Stack>

                              <Typography
                                variant="h6"
                                className="blog-title"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '1.15rem',
                                  color: '#f8fafc',
                                  lineHeight: 1.3,
                                  mb: 1,
                                  transition: 'color 0.2s ease',
                                }}
                              >
                                {post.title}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#94a3b8',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.6,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {post.excerpt || (post.content || '').replace(/<[^>]*>/g, '')}
                              </Typography>
                            </CardContent>
                          </BlogCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  /* Single Post Reader View */
                  <article>
                    {/* Top Action Bar */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                      <IconButton
                        onClick={() => handleSelectPost(null)}
                        sx={{
                          color: '#f8fafc',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' },
                        }}
                      >
                        <ArrowBack />
                      </IconButton>

                      <Stack direction="row" spacing={1}>
                        <IconButton
                          aria-label="share"
                          onClick={() => handleShare(selected)}
                          sx={{
                            color: '#94a3b8',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            '&:hover': { color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.15)' },
                          }}
                        >
                          <Share fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {/* Metadata Header */}
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                      <Chip
                        icon={<FolderOutlined sx={{ fontSize: '0.85rem !important', color: '#3b82f6 !important' }} />}
                        label={selected.category || 'General'}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(59, 130, 246, 0.15)',
                          color: '#60a5fa',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: '0.85rem' }} /> {formatDate(selected.createdAt)}
                      </Typography>
                    </Stack>

                    {/* Title */}
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: '1.8rem', sm: '2.4rem', md: '2.8rem' },
                        fontWeight: 900,
                        color: '#f8fafc',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        mb: 3,
                      }}
                    >
                      {selected.title}
                    </Typography>

                    {/* Banner Image */}
                    {selected.image ? (
                      <Box
                        sx={{
                          borderRadius: '20px',
                          overflow: 'hidden',
                          mb: 4,
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          boxShadow: '0 20px 40px -15px rgba(0,0,0,0.6)',
                        }}
                      >
                        <img
                          src={selected.image}
                          alt={selected.title}
                          style={{ width: '100%', maxHeight: 460, objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                    ) : null}

                    {/* HTML Content Body */}
                    <ArticleBody dangerouslySetInnerHTML={{ __html: selected.content || '<p>No content available.</p>' }} />

                    <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                    {/* SEO Keyword Badges */}
                    {selected.seoKeywords?.length > 0 && (
                      <Box sx={{ overflowX: 'auto', pb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1, fontWeight: 700 }}>
                          TOPICS & TAGS
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                          {selected.seoKeywords.map((k) => (
                            <Chip
                              key={k}
                              icon={<TagOutlined sx={{ fontSize: '0.8rem !important', color: '#94a3b8 !important' }} />}
                              label={k}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.03)',
                                color: '#94a3b8',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                fontSize: '0.75rem',
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </article>
                )}
              </GlassPaper>
            </Grid>
          </Grid>

          {/* Feedback Snackbar */}
          <Snackbar open={snack.open} autoHideDuration={4000} onClose={handleCloseSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={handleCloseSnack} severity={snack.severity} sx={{ width: '100%', borderRadius: '12px' }}>
              {snack.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default BlogReader;

// import React, { useEffect, useMemo, useState } from 'react';
// import {
//     Box,
//     Card,
//     CardContent,
//     CardMedia,
//     Chip,
//     Container,
//     Divider,
//     FormControl,
//     Grid,
//     IconButton,
//     InputAdornment,
//     MenuItem,
//     Paper,
//     Select,
//     Stack,
//     TextField,
//     Snackbar,
//     Alert,
//     Typography,
// } from '@mui/material';
// import { ArrowBack, Search, Share } from '@mui/icons-material';
// import { useParams, useNavigate } from 'react-router-dom';
// import { Helmet } from 'react-helmet-async';
// import { db } from '../firebaseConfig';
// import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

// const BlogReader = () => {
//     const [posts, setPosts] = useState([]);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [category, setCategory] = useState('All');
//     const [selected, setSelected] = useState(null);
//     const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    
//     const { id: routeId } = useParams();
//     const navigate = useNavigate();

//     const baseUrl = 'https://vikashtiwaridev.web.app';

//     useEffect(() => {
//         const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
//         const unsubscribe = onSnapshot(q, (snapshot) => {
//             setPosts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
//         });

//         return () => {
//             if (typeof unsubscribe === 'function') unsubscribe();
//         };
//     }, []);

//     // Sync routeId with selected post
//     useEffect(() => {
//         if (!posts.length) return;
//         if (routeId) {
//             const found = posts.find((p) => p.id === routeId);
//             if (found) {
//                 setSelected(found);
//             } else {
//                 setSelected(null);
//             }
//         } else {
//             setSelected(null);
//         }
//     }, [routeId, posts]);

//     const handleSelectPost = (post) => {
//         setSelected(post);
//         if (post) {
//             navigate(`/blog/${post.id}`);
//         } else {
//             navigate('/blog');
//         }
//     };

//     const filtered = useMemo(() => {
//         const q = (searchQuery || '').trim().toLowerCase();
//         const selectedCat = (category || 'All').toString().trim().toLowerCase();

//         return posts.filter((p) => {
//             const pCat = (p.category || 'General').toString().trim().toLowerCase();
//             if (selectedCat !== 'all' && pCat !== selectedCat) return false;

//             if (!q) return true;
//             const hay = [p.title, p.excerpt, (p.seoKeywords || []).join(' '), p.content]
//                 .filter(Boolean)
//                 .join(' ')
//                 .toLowerCase();
//             return hay.includes(q);
//         });
//     }, [posts, searchQuery, category]);

//     const categories = useMemo(() => {
//         const set = new Set(['All', 'General', 'Technology', 'Design', 'Lifestyle', 'Business', 'Education']);
//         posts.forEach((p) => set.add(p.category || 'General'));
//         return Array.from(set);
//     }, [posts]);

//     const formatDate = (timestamp) => {
//         if (!timestamp) return '';
//         let date = null;
//         if (typeof timestamp.toDate === 'function') date = timestamp.toDate();
//         else if (timestamp && timestamp.seconds) date = new Date(timestamp.seconds * 1000);
//         else date = new Date(timestamp);

//         try {
//             return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
//         } catch (e) {
//             return String(date);
//         }
//     };

//     const handleShare = async (post) => {
//         if (!post) return;
//         const shareUrl = `${baseUrl}/blog/${post.id}`;

//         if (navigator.share) {
//             try {
//                 await navigator.share({ title: post.title, text: post.excerpt || '', url: shareUrl });
//                 setSnack({ open: true, message: 'Shared successfully', severity: 'success' });
//                 return;
//             } catch (e) {
//                 // fall through to clipboard
//             }
//         }

//         try {
//             await navigator.clipboard.writeText(shareUrl);
//             setSnack({ open: true, message: 'Link copied to clipboard', severity: 'success' });
//         } catch (e) {
//             window.prompt('Copy this link', shareUrl);
//             setSnack({ open: true, message: 'Unable to copy automatically — prompt shown', severity: 'warning' });
//         }
//     };

//     const handleCloseSnack = () => setSnack((s) => ({ ...s, open: false }));

//     // SEO Data Generation
//     const currentCanonicalUrl = selected ? `${baseUrl}/blog/${selected.id}` : `${baseUrl}/blog`;
//     const pageTitle = selected 
//         ? `${selected.title} | Vikash Tiwari Blog` 
//         : 'Tech Blog & Articles | Vikash Tiwari - Software Developer';
//     const pageDescription = selected 
//         ? (selected.excerpt || (selected.content || '').replace(/<[^>]*>/g, '').slice(0, 160)) 
//         : 'Technical tutorials, software development insights, Android, Flutter, and web engineering case studies by Vikash Tiwari.';
//     const pageImage = selected?.image || `${baseUrl}/VikashTiwariIcon.png`;

//     // Schema Markup
//     const structuredData = selected ? {
//         "@context": "https://schema.org",
//         "@type": "BlogPosting",
//         "headline": selected.title,
//         "description": pageDescription,
//         "image": selected.image ? [selected.image] : [],
//         "datePublished": selected.createdAt?.seconds ? new Date(selected.createdAt.seconds * 1000).toISOString() : new Date().toISOString(),
//         "author": {
//             "@type": "Person",
//             "name": "Vikash Tiwari",
//             "url": baseUrl
//         },
//         "publisher": {
//             "@type": "Person",
//             "name": "Vikash Tiwari"
//         },
//         "mainEntityOfPage": {
//             "@type": "WebPage",
//             "@id": currentCanonicalUrl
//         },
//         "keywords": (selected.seoKeywords || []).join(', ')
//     } : {
//         "@context": "https://schema.org",
//         "@type": "Blog",
//         "name": "Vikash Tiwari Tech Blog",
//         "description": pageDescription,
//         "url": currentCanonicalUrl,
//         "author": {
//             "@type": "Person",
//             "name": "Vikash Tiwari"
//         }
//     };

//     return (
//         <>
//             <Helmet>
//                 {/* Meta Tags */}
//                 <title>{pageTitle}</title>
//                 <meta name="description" content={pageDescription} />
//                 <link rel="canonical" href={currentCanonicalUrl} />
//                 {selected?.seoKeywords?.length > 0 && (
//                     <meta name="keywords" content={selected.seoKeywords.join(', ')} />
//                 )}

//                 {/* Open Graph */}
//                 <meta property="og:type" content={selected ? "article" : "website"} />
//                 <meta property="og:title" content={pageTitle} />
//                 <meta property="og:description" content={pageDescription} />
//                 <meta property="og:url" content={currentCanonicalUrl} />
//                 <meta property="og:image" content={pageImage} />

//                 {/* Twitter */}
//                 <meta name="twitter:card" content="summary_large_image" />
//                 <meta name="twitter:title" content={pageTitle} />
//                 <meta name="twitter:description" content={pageDescription} />
//                 <meta name="twitter:image" content={pageImage} />

//                 {/* Structured JSON-LD */}
//                 <script type="application/ld+json">
//                     {JSON.stringify(structuredData)}
//                 </script>
//             </Helmet>

//             <Container maxWidth="xl" sx={{ py: 6 }} className="prose prose-lg dark:prose-invert mx-auto px-4">
//                 <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} mb={4}>
//                     <Box>
//                         <Typography variant="h1" sx={{ fontSize: '2.25rem', fontWeight: 800 }} mb={1}>
//                             Blog
//                         </Typography>
//                         <Typography color="text.secondary">Thoughts, tutorials, and case studies.</Typography>
//                     </Box>
//                     <Stack direction="row" spacing={1} alignItems="center">
//                         <FormControl size="small" sx={{ minWidth: 160 }}>
//                             <Select value={category} onChange={(e) => setCategory(e.target.value)}>
//                                 {['All', 'General', 'Technology', 'Design', 'Lifestyle', 'Business', 'Education'].map((c) => (
//                                     <MenuItem key={c} value={c}>{c}</MenuItem>
//                                 ))}
//                             </Select>
//                         </FormControl>
//                     </Stack>
//                 </Stack>

//                 <Grid container spacing={4}>
//                     <Grid item xs={12} md={4}>
//                         <Paper sx={{ p: 2, borderRadius: 4 }} className="tw-card p-4">
//                             <TextField
//                                 placeholder="Search posts"
//                                 value={searchQuery}
//                                 onChange={(e) => setSearchQuery(e.target.value)}
//                                 fullWidth
//                                 InputProps={{
//                                     startAdornment: (
//                                         <InputAdornment position="start">
//                                             <Search />
//                                         </InputAdornment>
//                                     ),
//                                 }}
//                                 sx={{ mb: 2 }}
//                             />

//                             <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
//                                 {categories.map((c) => (
//                                     <Chip
//                                         key={c}
//                                         label={c}
//                                         clickable
//                                         color={c === category ? 'primary' : 'default'}
//                                         onClick={() => setCategory(c)}
//                                         sx={{ mb: 1, borderRadius: 2 }}
//                                     />
//                                 ))}
//                             </Stack>

//                             <Divider sx={{ my: 1 }} />

//                             <Stack spacing={2} mt={2}>
//                                 {filtered.length === 0 ? (
//                                     <Typography color="text.secondary">No posts found.</Typography>
//                                 ) : (
//                                     filtered.map((post) => (
//                                         <Card
//                                             key={post.id}
//                                             variant="outlined"
//                                             sx={{
//                                                 display: 'flex',
//                                                 cursor: 'pointer',
//                                                 borderRadius: 3,
//                                                 transition: 'transform 200ms ease, box-shadow 200ms ease',
//                                                 '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
//                                             }}
//                                             className="bg-slate-800/50"
//                                             onClick={() => handleSelectPost(post)}
//                                         >
//                                             {post.image ? (
//                                                 <CardMedia component="img" image={post.image} alt={post.title} sx={{ width: 120, height: 84, objectFit: 'cover' }} />
//                                             ) : null}
//                                             <CardContent sx={{ flex: 1 }}>
//                                                 <Typography variant="subtitle1" fontWeight={700} noWrap>
//                                                     {post.title}
//                                                 </Typography>
//                                                 <Typography variant="caption" color="text.secondary">
//                                                     {(post.excerpt || '').slice(0, 90)}
//                                                 </Typography>
//                                                 <Typography variant="caption" display="block" color="text.secondary">
//                                                     {formatDate(post.createdAt)} • {post.category || 'General'}
//                                                 </Typography>
//                                             </CardContent>
//                                         </Card>
//                                     ))
//                                 )}
//                             </Stack>
//                         </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={8}>
//                         <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }} className="tw-card p-6">
//                             {!selected ? (
//                                 <Box>
//                                     <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700 }} mb={2}>
//                                         Latest Posts
//                                     </Typography>
//                                     <Grid container spacing={3}>
//                                         {filtered.map((post) => (
//                                             <Grid item xs={12} sm={6} key={post.id}>
//                                                 <Card
//                                                     sx={{
//                                                         height: '100%',
//                                                         borderRadius: 3,
//                                                         transition: 'transform 180ms ease, box-shadow 180ms ease',
//                                                         '&:hover': { transform: 'translateY(-6px)', boxShadow: 8 },
//                                                     }}
//                                                     className="bg-slate-800/40"
//                                                     onClick={() => handleSelectPost(post)}
//                                                 >
//                                                     {post.image ? <CardMedia component="img" height="160" image={post.image} alt={post.title} /> : null}
//                                                     <CardContent>
//                                                         <Stack direction="row" justifyContent="space-between" alignItems="center">
//                                                             <Chip label={post.category || 'General'} size="small" />
//                                                             <Typography variant="caption" color="text.secondary">{formatDate(post.createdAt)}</Typography>
//                                                         </Stack>
//                                                         <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700 }} mt={1} mb={1}>
//                                                             {post.title}
//                                                         </Typography>
//                                                         <Typography color="text.secondary">{post.excerpt || (post.content || '').replace(/<[^>]*>/g, '').slice(0, 140)}</Typography>
//                                                     </CardContent>
//                                                 </Card>
//                                             </Grid>
//                                         ))}
//                                     </Grid>
//                                 </Box>
//                             ) : (
//                                 <article>
//                                     <Stack direction="row" spacing={1} alignItems="center" mb={2}>
//                                         <IconButton onClick={() => handleSelectPost(null)}>
//                                             <ArrowBack />
//                                         </IconButton>
//                                         <Box sx={{ pr: 2 }}>
//                                             <Typography variant="h1" sx={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
//                                                 {selected.title}
//                                             </Typography>
//                                             <Typography color="text.secondary">{formatDate(selected.createdAt)} • {selected.category || 'General'}</Typography>
//                                         </Box>
//                                         <Box sx={{ flex: 1 }} />
//                                         <Stack direction="row" spacing={1} alignItems="center">
//                                             <IconButton aria-label="share" onClick={() => handleShare(selected)}>
//                                                 <Share />
//                                             </IconButton>
//                                         </Stack>
//                                     </Stack>

//                                     {selected.image ? (
//                                         <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
//                                             <img src={selected.image} alt={selected.title} style={{ width: '100%', maxHeight: 420, objectFit: 'cover' }} />
//                                         </Box>
//                                     ) : null}

//                                     <Box sx={{ mb: 2 }} dangerouslySetInnerHTML={{ __html: selected.content || '<p>No content</p>' }} />

//                                     <Divider sx={{ my: 2 }} />

//                                     <Box sx={{ overflowX: 'auto', pb: 1 }}>
//                                         <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
//                                             {(selected.seoKeywords || []).map((k) => (
//                                                 <Chip key={k} label={k} variant="outlined" />
//                                             ))}
//                                         </Stack>
//                                     </Box>
//                                 </article>
//                             )}
//                         </Paper>
//                     </Grid>
//                 </Grid>
//                 <Snackbar open={snack.open} autoHideDuration={4000} onClose={handleCloseSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
//                     <Alert onClose={handleCloseSnack} severity={snack.severity} sx={{ width: '100%' }}>
//                         {snack.message}
//                     </Alert>
//                 </Snackbar>
//             </Container>
//         </>
//     );
// };

// export default BlogReader;
